// A reveal whose resolved start is 0 never crosses it at scrollY 0, so bands already in view stay pre-reveal until the visitor scrolls.
// ix3 registers its triggers progressively, so the poll runs until the trigger set has held still for a quiet period; each trigger fires
// at most once. A page that loads no ScrollTrigger script has nothing to fire, so it is not polled.

const POLL_INTERVAL = 250;
const QUIET_POLLS = 3;
const POLL_CEILING = 60000;

const fired = new WeakSet();

function isUnresolvedOnLoad(trigger, environment) {
  if (trigger.start !== 0 || trigger.progress !== 0) return false;
  if (typeof trigger.vars?.onEnter !== 'function') return false;
  if (fired.has(trigger)) return false;

  const box = trigger.trigger?.getBoundingClientRect?.();
  return Boolean(box) && box.top < environment.innerHeight && box.bottom > 0;
}

function loadsScrollTrigger(environment) {
  const query = environment.document?.querySelector;
  return typeof query !== 'function' || Boolean(environment.document.querySelector('script[src*="ScrollTrigger"]'));
}

function triggerCount(environment) {
  return environment.ScrollTrigger?.getAll?.()?.length ?? 0;
}

export function resolveLoadReveals(environment = globalThis) {
  const triggers = environment.ScrollTrigger?.getAll?.();
  if (!triggers?.length) return null;

  let resolved = 0;
  for (const trigger of triggers) {
    if (!isUnresolvedOnLoad(trigger, environment)) continue;
    fired.add(trigger);
    trigger.vars.onEnter(trigger);
    resolved += 1;
  }

  return resolved;
}

export function initializeLoadReveals(environment = globalThis) {
  if (typeof environment.setTimeout !== 'function' || !loadsScrollTrigger(environment)) return resolveLoadReveals(environment);

  let stopped = false;
  let quiet = 0;
  let seen = 0;
  let elapsed = 0;

  environment.addEventListener?.('pagehide', () => { stopped = true; }, { once: true });
  environment.addEventListener?.('load', () => resolveLoadReveals(environment), { once: true });
  environment.document?.fonts?.ready?.then?.(() => resolveLoadReveals(environment));

  const attempt = () => {
    if (stopped) return;

    const resolved = resolveLoadReveals(environment);
    const count = triggerCount(environment);
    if (resolved || count !== seen) quiet = 0;
    else if (resolved === 0) quiet += 1;
    seen = count;

    if (quiet >= QUIET_POLLS || elapsed >= POLL_CEILING) return;
    elapsed += POLL_INTERVAL;
    environment.setTimeout(attempt, POLL_INTERVAL);
  };

  attempt();
}
